// utils/dateHelper.js

/**
 * Formata uma data usando `Intl.DateTimeFormat`, com fallback de opções e timezone padrão
 * para 'America/Sao_Paulo'. Se qualquer opção granular (ex.: `year`, `month`, `dateStyle`, etc.)
 * for fornecida em `options`, mantém apenas `timeZone` padrão e respeita as opções informadas;
 * caso contrário, aplica `{ dateStyle: 'short', timeZone: 'America/Sao_Paulo' }`.
 *
 * @param {string|number|Date} dateString
 *        Valor aceito por `new Date()`. Se falsy, retorna `''`.
 * @param {string} [locale='pt-BR']
 *        Locale BCP 47 (ex.: `'pt-BR'`, `'en-US'`).
 * @param {Intl.DateTimeFormatOptions} [options={}]
 *        Opções para `Intl.DateTimeFormat`. Ver regra acima para `dateStyle`/granulares.
 * @returns {string}
 *        Data formatada ou a string original se ocorrer erro de formatação.
 *
 * @example
 * formatDate('2025-11-11') // '11/11/2025' (pt-BR, short)
 * @example
 * formatDate('2025-11-11T13:45:00Z', 'en-US', { year: 'numeric', month: 'long', day: '2-digit' })
 * // 'November 11, 2025'
 */
export function formatDate(dateString, locale = 'pt-BR', options = {}) {
    if (!dateString) return '';

    const defaultOptions = {
        dateStyle: 'short',
        timeZone: 'America/Sao_Paulo',
    };
    const hasGranularOrStyles = [
      'year','month','day','weekday','era','hour','minute','second','timeStyle','dateStyle'
    ].some(k => Object.prototype.hasOwnProperty.call(options, k));

    const baseOptions = hasGranularOrStyles ? { timeZone: defaultOptions.timeZone } : defaultOptions;
    const formatOptions = { ...baseOptions, ...options };


  try {
        return new Intl.DateTimeFormat(locale, formatOptions).format(new Date(dateString));
    } catch (error) {
        console.error('Erro ao formatar a data:', error);
        return dateString; // Retorna a string original em caso de erro
    }
}

/**
 * Formata um objeto `Date` como `YYYY-MM-DD` usando os componentes **locais**
 * (ano/mês/dia), sem conversão para UTC. Útil para preencher inputs `type="date"`
 * a partir de datas construídas localmente (ex.: `new Date(ano, mes, 1)`), evitando
 * o deslocamento de dia que `toDateOnly` (baseado em `toISOString`) pode causar.
 *
 * @param {Date} data
 *        Objeto `Date`. Se falsy, retorna `''`.
 * @returns {string}
 *        Data no formato `YYYY-MM-DD` ou `''` se a data for inválida.
 *
 * @example
 * toLocalDateOnly(new Date(2025, 2, 1)) // '2025-03-01'
 */
export function toLocalDateOnly(data) {
  if (!data || Number.isNaN(data.getTime())) {
    return '';
  }

  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
}

/**
 * Recebe uma data com hora/timezone e retorna apenas a data yy-MM-dd
 *
 * @param {string|number|Date} data
 *        Data em formato 2025-11-11T13:45:00Z ou similar. Se falsy, retorna `null`.
 * @returns {string}
 *        Data formatada ou a string original se ocorrer erro de formatação.
 *
 * @example
 * toDateOnly('2025-11-11T13:45:00Z) // '2025-11-11' (pt-BR, short)
 */
export function toDateOnly(data) {
  if (!data) {
    return null
  }

  const date = new Date(data)

  if (Number.isNaN(date.getTime())) {
    return null
  }

  return date.toISOString().slice(0, 10)
}



export function formatDateMonthYear(dateString, locale = 'pt-BR', options = {}) {
    if (!dateString) return '';

    const defaultOptions = {
        month: '2-digit',      // ou 'long' para "novembro"
        year: 'numeric',
        timeZone: 'America/Sao_Paulo',
    };

    const hasGranularOrStyles = [
        'year','month','day','weekday','era','hour','minute','second','timeStyle','dateStyle'
    ].some(k => Object.prototype.hasOwnProperty.call(options, k));

    const baseOptions = hasGranularOrStyles ? { timeZone: defaultOptions.timeZone } : defaultOptions;
    const formatOptions = { ...baseOptions, ...options };


    try {
        return new Intl.DateTimeFormat(locale, formatOptions).format(new Date(dateString));
    } catch (error) {
        console.error('Erro ao formatar a data:', error);
        return dateString; // Retorna a string original em caso de erro
    }
}


/**
 * Formata uma lista de datas separadas por vírgula, aplicando `formatDate` em cada item,
 * e retorna uma única string com as datas formatadas separadas por vírgula e espaço.
 *
 * Observação: utiliza o comportamento/valores padrão de `formatDate`.
 *
 * @param {string} dateString
 *        String contendo uma ou mais datas separadas por vírgula. Se falsy, retorna `''`.
 * @param {string} [locale='pt-BR']
 *        (Ignorado; mantido apenas por compatibilidade de assinatura.)
 * @param {Intl.DateTimeFormatOptions} [options={}]
 *        (Ignorado; mantido apenas por compatibilidade de assinatura.)
 * @returns {string}
 *        Datas formatadas unidas por `', '`.
 *
 * @example
 * formatMultipleDate('2025-11-11,2025-11-12')
 * // '11/11/2025, 12/11/2025'
 */
export function formatMultipleDate(dateString, locale = 'pt-BR', options = {}) {
    if (!dateString) return '';

    let dates = dateString.split(',')

    let formatedDates = []

    for (var i = 0; i < dates.length; i++ ){
        let date = dates[i]

        formatedDates.push(formatDate(date));
    }

    return  formatedDates.join(', ')
}

/**
 * Formata uma data e hora usando `Intl.DateTimeFormat` com opções padrão:
 * `{ dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' }`.
 *
 * @param {string|number|Date} dateString
 *        Valor aceito por `new Date()`. Se falsy, retorna `''`.
 * @param {string} [locale='pt-BR']
 *        Locale BCP 47 (ex.: `'pt-BR'`, `'en-US'`).
 * @param {Intl.DateTimeFormatOptions} [options={}]
 *        Opções adicionais para `Intl.DateTimeFormat` (sobrescrevem as padrão).
 * @returns {string}
 *        Data/hora formatada ou a string original em caso de erro.
 *
 * @example
 * formatDateTime('2025-11-11T13:45:00Z')
 * // '11/11/2025 10:45' (exemplo; depende do fuso/locale)
 */
export function formatDateTime(dateString, locale = 'pt-BR', options = {}) {
    if (!dateString) return '';

    const defaultOptions = {
        dateStyle: 'short',
        timeStyle: 'short',
        timeZone: 'America/Sao_Paulo',
    };
    const formatOptions = { ...defaultOptions, ...options };

    try {
        return new Intl.DateTimeFormat(locale, formatOptions).format(new Date(dateString));
    } catch (error) {
        console.error('Erro ao formatar a data:', error);
        return dateString; // Retorna a string original em caso de erro
    }
}

/**
 * Subtrai um número de meses de um objeto `Date`, ajustando ano/mês conforme necessário.
 * **Atenção:** esta função **muta** o objeto `Date` recebido.
 *
 * @param {Date} data
 *        Objeto `Date` que será modificado.
 * @param {number} meses
 *        Quantidade de meses a subtrair (inteiro; pode ser > 12).
 * @returns {Date}
 *        O mesmo objeto `Date` recebido, após o ajuste.
 *
 * @example
 * const d = new Date('2025-03-31');
 * subtrairMeses(d, 1) // 2025-02-28/29 (dependendo do ano)
 */
export function subtrairMeses(data, meses) {
    // Ajusta o ano e o mês se necessário
    const ano = data.getFullYear();
    const mes = data.getMonth() - meses;

    data.setMonth(mes);

    // Se o mês for negativo, ajuste o ano e o mês
    if (mes < 0) {
        data.setFullYear(ano - Math.ceil(Math.abs(mes) / 12));
    }

    return data;
}

/**
 * Calcula a diferença inteira em dias entre duas datas (dataFinal - dataInicial),
 * normalizando ambas para meia-noite em UTC (zera horas/min/seg/ms).
 *
 * @param {string|number|Date} dataInicial
 *        Valor aceito por `new Date()`.
 * @param {string|number|Date} dataFinal
 *        Valor aceito por `new Date()`.
 * @returns {number}
 *        Diferença em dias (pode ser negativa, zero ou positiva).
 *
 * @example
 * diferencaEmDias('2025-11-10', '2025-11-11') // 1
 * @example
 * diferencaEmDias('2025-11-11T23:59:59Z', '2025-11-12T00:00:01Z') // 1 (horas desconsideradas)
 */
export function diferencaEmDias(dataInicial, dataFinal) {
    // Converter as strings ISO ou objetos Date para objetos Date
    const inicio = new Date(dataInicial);
    const fim = new Date(dataFinal);

    // Garantir que ambas as datas estão em UTC (zerar horas, minutos, etc.)
    inicio.setUTCHours(0, 0, 0, 0);
    fim.setUTCHours(0, 0, 0, 0);

    // Calcular a diferença em milissegundos
    const diferencaMillis = fim - inicio;

    // Converter a diferença de milissegundos para dias
    const diferencaDias = diferencaMillis / (1000 * 60 * 60 * 24);

    return diferencaDias;
}

/**
 * Converte uma data em uma expressão relativa em português (pt-BR), como
 * "Agora mesmo", "Há X minutos/horas/dias atrás".
 *
 * @param {string|number|Date} data
 *        Valor aceito por `new Date()`. Se não for `Date`, será convertido.
 * @returns {string}
 *        Texto relativo em pt-BR. Para datas futuras, retorna "Agora mesmo".
 *
 * @example
 * DataParaTempoAtras(new Date(Date.now() - 90_000)) // 'Há 1 minuto atrás'
 */
export function DataParaTempoAtras(data) {
    if (!(data instanceof Date)) {
        data = new Date(data);
    }

    const agora = new Date();
    const diferencaEmMilissegundos = agora - data;

    if (diferencaEmMilissegundos < 0) {
        return "Agora mesmo";
    }

    const minutos = Math.floor(diferencaEmMilissegundos / (1000 * 60));
    const horas = Math.floor(diferencaEmMilissegundos / (1000 * 60 * 60));
    const dias = Math.floor(diferencaEmMilissegundos / (1000 * 60 * 60 * 24));

    if (minutos < 1) {
        return "Agora mesmo";
    } else if (minutos < 60) {
        return `Há ${minutos} minuto${minutos > 1 ? "s" : ""} atrás`;
    } else if (horas < 24) {
        return `Há ${horas} hora${horas > 1 ? "s" : ""} atrás`;
    } else {
        return `Há ${dias} dia${dias > 1 ? "s" : ""} atrás`;
    }
}

/**
 * Formata uma data **no fuso horário de Brasília** ('America/Sao_Paulo') usando `Intl.DateTimeFormat`.
 * Se a data já estiver efetivamente nesse fuso (comparação via `toLocaleString`), apenas formata;
 * caso contrário, converte para o fuso de Brasília antes de formatar.
 *
 * @param {string|number|Date} dateString
 *        Valor aceito por `new Date()`. Se falsy, retorna `''`.
 * @param {string} [locale='pt-BR']
 *        Locale BCP 47.
 * @param {Intl.DateTimeFormatOptions} [options={}]
 *        Opções adicionais para `Intl.DateTimeFormat`. Padrão:
 *        `{ dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' }`.
 * @returns {string}
 *        Data formatada em horário de Brasília, ou a string original em caso de erro.
 *
 * @example
 * DateToAmericaSaoPaulo('2025-11-11T12:00:00Z')
 * // '11/11/2025 09:00' (exemplo; depende do locale)
 */
export function DateToAmericaSaoPaulo(dateString, locale = 'pt-BR', options = {}) {
    if (!dateString) return '';

    const defaultOptions = {
        dateStyle: 'short',
        timeStyle: 'short',
        timeZone: 'America/Sao_Paulo', // Força o horário de Brasília
    };
    const formatOptions = { ...defaultOptions, ...options };

    try {
        // Converte a data para o objeto Date
        const date = new Date(dateString);

        // Verifica se a data já está em horário de Brasília
        const isBrasiliaTime = date.toLocaleString(locale, { timeZone: 'America/Sao_Paulo' }) === date.toLocaleString(locale);
        if (isBrasiliaTime) {
            return new Intl.DateTimeFormat(locale, formatOptions).format(date);
        }

        // Converte para o horário de Brasília usando a localidade fornecida
        const brDate = new Date(
            date.toLocaleString(locale, { timeZone: 'America/Sao_Paulo' })
        );

        return new Intl.DateTimeFormat(locale, formatOptions).format(brDate);
    } catch (error) {
        console.error('Erro ao converter a data:', error);
        return dateString; // Retorna a string original em caso de erro
    }
}


/**
 * Converte uma data para um objeto `Date` correspondente ao horário local de Brasília
 * ('America/Sao_Paulo'), preservando a representação exibida para esse fuso.
 *
 * @param {string|number|Date} dateString
 *        Valor aceito por `new Date()`. Se falsy, retorna `''`.
 * @param {string} [locale='pt-BR']
 *        Locale BCP 47 usado na conversão via `toLocaleString`.
 * @param {Intl.DateTimeFormatOptions} [options={}]
 *        (Ignorado; mantido por compatibilidade de assinatura.)
 * @returns {Date}
 *        Objeto `Date` representando a data/hora em 'America/Sao_Paulo'.
 * @throws {Error}
 *        Repassa erros de conversão.
 *
 * @example
 * DateToDateAmericaSaoPaulo('2025-11-11T12:00:00Z') // Date ajustado para o horário de Brasília
 */
export function DateToDateAmericaSaoPaulo(dateString, locale = 'pt-BR', options = {}) {
    if (!dateString) return '';

    const defaultOptions = {
        dateStyle: 'short',
        timeStyle: 'short',
        timeZone: 'America/Sao_Paulo', // Força o horário de Brasília
    };
    const formatOptions = { ...defaultOptions, ...options };

    try {
        // Converte a data para o objeto Date
        const date = new Date(dateString);

        // Verifica se a data já está em horário de Brasília
        const isBrasiliaTime = date.toLocaleString(locale, { timeZone: 'America/Sao_Paulo' }) === date.toLocaleString(locale);
        if (isBrasiliaTime) {
            return date;
        }

        // Converte para o horário de Brasília usando a localidade fornecida
        const brDate = new Date(
            date.toLocaleString(locale, { timeZone: 'America/Sao_Paulo' })
        );

        return brDate;
    } catch (error) {
        console.error('Erro ao converter a data:', error);
        throw  error
    }
}

/**
 * Aplica a "regra dos 2 dias úteis" e devolve a data cuja competência (mês/ano) deve ser
 * tratada como **mês atual** nos dashboards.
 *
 * Os dashboards não viram de mês no dia 1: os 2 primeiros dias úteis do mês novo ainda
 * consideram o mês anterior como "atual", porque o fechamento do mês anterior ainda está
 * em curso. Se o mês vira em fim de semana, a graça cobre o próprio fim de semana mais os
 * 2 dias úteis subsequentes. Espelha `MesReferenciaComGracaDiasUteis` do
 * `DashboardTelevisaoRepository` — os labels precisam acompanhar os dados que a API devolve.
 *
 * O cálculo é feito em 'America/Sao_Paulo' para que o dia da semana e a virada de mês não
 * dependam do fuso da máquina que abre o dashboard.
 *
 * @param {Date} [hoje=new Date()]
 *        Data de referência. Se inválida, usa a data atual.
 * @returns {Date}
 *        `Date` (à meia-noite local) do mês/ano que deve ser exibido como mês atual.
 *
 * @example
 * // 01/09/2026 (terça) -> 28/08/2026: dashboard ainda mostra Agosto
 * mesReferenciaComGracaDiasUteis(new Date(2026, 8, 1))
 * @example
 * // 08/09/2026 (terça) -> 04/09/2026: já dentro de Setembro
 * mesReferenciaComGracaDiasUteis(new Date(2026, 8, 8))
 */
export function mesReferenciaComGracaDiasUteis(hoje = new Date()) {
  const base = hoje instanceof Date && !Number.isNaN(hoje.getTime()) ? hoje : new Date();

  // Extrai ano/mês/dia em Brasília e remonta como data local, para ler o dia da semana correto.
  const partes = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .formatToParts(base)
    .reduce((acc, { type, value }) => ({ ...acc, [type]: Number(value) }), {});

  const referencia = new Date(partes.year, partes.month - 1, partes.day);

  // Domingo volta 3 dias; segunda e terça voltam 4 (para atravessar o fim de semana);
  // de quarta a sábado bastam 2 dias.
  const diasParaVoltar = { 0: 3, 1: 4, 2: 4 }[referencia.getDay()] ?? 2;

  referencia.setDate(referencia.getDate() - diasParaVoltar);
  return referencia;
}

const diaEmSaoPaulo = (data) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(data);

export function diasAteData(data, referencia = new Date()) {
  if (!data) return null;

  const alvo = new Date(data);
  if (Number.isNaN(alvo.getTime())) return null;

  const meiaNoite = (d) => Date.parse(`${diaEmSaoPaulo(d)}T00:00:00Z`);
  return Math.round((meiaNoite(alvo) - meiaNoite(referencia)) / 86400000);
}
